import type { BehaviorSubject } from "rxjs";
import { SelectedMapObject } from "@iventis/map-types";
import type { AnyLayerStyle, StylePropertyToValueMapV2 } from "../../types/style-types";
import type { MapState } from "../../types/store-schema";
import { LayerSourceType } from "../../types/internal";
import { removeLayerIdSuffixes } from "../engine-generic";
import { removeAdditionalLayerSuffixes } from "../../utilities/layer.helpers";

export abstract class Layer<TSource, TStyle extends Partial<AnyLayerStyle>> {
    protected abstract layerId: string;

    protected abstract sourceId: string;

    protected isDrawing = false;

    protected readonly state$: BehaviorSubject<MapState>;

    constructor(state$: BehaviorSubject<MapState>) {
        this.state$ = state$;
    }

    protected abstract addLayer(layerSourceType: LayerSourceType): void;

    protected abstract removeLayer(): void;

    protected abstract addLocalSource(): void;

    public abstract updateLocalSource(source: TSource, drawingSource?: TSource): void;

    protected abstract removeLocalSource(): void;

    protected abstract updateStyle(changes: StylePropertyToValueMapV2<TStyle>): void;

    protected abstract hideLayer(): void;

    protected abstract showLayer(): void;

    protected abstract updateMapOrder(aboveLayerId: string): void;

    protected abstract updateMapLevel(level: number): void;

    public abstract getLayerIdsForSnapping(): string[];

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    public onDrawingStarted(objectIds: string[]): void {
        this.isDrawing = true;
    }

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    public onDrawingFinished(objectIds: string[]): void {
        this.isDrawing = false;
    }

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    public onTileOnlyMapObjectsUpdated(objectIds: string[]): void {
        // By default, do nothing
    }

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    public hideTileOnlyMapObjects(objectIds: string[]): void {
        // By default, do nothing
    }

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    public deleteMapObjects(deleteMapObjectIds: string[]) {
        // By default, do nothing
    }

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    public restoreMapObjects(restoredMapObjectIds: string[]) {
        // By default, do nothing
    }

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    public highlightObjects(objectIds: string[] | TSource) {
        // By default, do nothing
    }

    public removeHighlights() {
        // By default, do nothing
    }

    public getIventisLayer() {
        const iventisLayerId = removeAdditionalLayerSuffixes(removeLayerIdSuffixes(this.layerId));
        return this.state$.value.layers.value.find((layer) => layer.id === iventisLayerId);
    }

    public getGeoJson(): TSource | null {
        return this.state$.value.geoJSON.value[this.layerId]?.map(({ feature }) => feature) as TSource;
    }

    public getSelectedMapObjects(): SelectedMapObject[] {
        return this.state$.value.mapObjectsSelected.value.filter((object) => object.layerId === this.layerId);
    }
}
